import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const offerNotSellingArticle: BlogArticle = {
  key: BlogArticleKey.OFFER_NOT_SELLING,
  slug: BlogSlugs.OFFER_NOT_SELLING,
  categoryId: "02",
  date: "2026-08-30",
  readTime: 7,
  locales: {
    fr: {
      title: "Ton offre ne se vend pas, et ce n'est presque jamais le prix",
      excerpt:
        "Baisser le prix est la première réaction, et la moins efficace. Quand une offre ne se vend pas, le problème est presque toujours en amont du chiffre.",
      description:
        "Les quatre raisons pour lesquelles une offre ne se vend pas, comment les distinguer, et pourquoi la réponse est dans tes appels.",
      ctaTitle: "Corrige ton offre sur ce qui a été dit",
      ctaText:
        "On repart de tes appels pour voir où ça bloque : le problème, la promesse, la preuve ou le prix. Puis on réécrit l'offre avec les mots de tes clients. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les rendez-vous se passent bien. Les gens écoutent, trouvent ça intéressant, demandent une proposition. Et ça s'arrête là. Au bout de quelques mois, la conclusion tombe toute seule : c'est trop cher.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est la conclusion la plus rassurante, parce qu'elle se corrige en une minute. C'est aussi celle qui est fausse dans la grande majorité des cas.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un prix est jugé trop élevé quand la valeur n'est pas claire. Le problème n'est donc presque jamais le chiffre, c'est ce qui aurait dû le rendre évident.",
        },
        { type: ArticleBlockType.H2, text: "Les quatre blocages, et comment les distinguer" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une offre qui ne se vend pas bloque à l'un de ces quatre endroits. Ils ne se corrigent pas de la même façon, et la première tâche est de savoir lequel te concerne.",
        },
        { type: ArticleBlockType.H3, text: "1. Le problème n'est pas reconnu" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tu décris un problème que la personne ne vit pas, ou qu'elle vit sans le nommer ainsi. Le signe : on t'écoute poliment, on trouve ça intelligent, et personne ne dit *c'est exactement ça*. Aucune promesse ne rattrapera un problème que personne ne reconnaît comme le sien.",
        },
        { type: ArticleBlockType.H3, text: "2. Le problème est reconnu mais pas prioritaire" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Là, on te dit *oui, c'est vrai*, puis rien ne se passe. Le problème existe, il n'est simplement pas assez cher pour passer devant les dix autres. Le signe : les gens repoussent sans jamais dire non. Ce qui manque n'est pas un argument, c'est un chiffre.",
        },
        { type: ArticleBlockType.H3, text: "3. La promesse n'est pas crédible" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le problème est reconnu, il est prioritaire, et on ne croit pas que tu le règles. Le signe : beaucoup de questions sur le comment, sur la durée, sur ce qui se passe si ça ne marche pas. Ce n'est pas un manque d'envie, c'est un manque de preuve.",
        },
        { type: ArticleBlockType.H3, text: "4. Le prix, vraiment" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ça arrive, et c'est reconnaissable : la personne veut, comprend, croit, et n'a pas le budget. Le signe qui ne trompe pas, c'est qu'elle demande une version réduite ou un étalement. Quand on te dit simplement *c'est trop cher* sans rien proposer, tu es dans un des trois cas précédents.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que tu entends", "Ce qui bloque en réalité"],
          rows: [
            ["« Intéressant, je vais y réfléchir »", "Le problème n'est pas reconnu"],
            ["« C'est vrai, mais là on est sur autre chose »", "Il est reconnu, pas prioritaire"],
            ["« Et concrètement, ça se passe comment ? »", "La promesse manque de preuve"],
            ["« Vous auriez une formule plus légère ? »", "Le prix, celui-là est vrai"],
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Pourquoi baisser le prix aggrave trois cas sur quatre",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si le problème n'est pas reconnu, un prix plus bas ne le fait pas apparaître. S'il n'est pas prioritaire, un prix plus bas ne le fait pas monter dans la liste. Si la promesse n'est pas crédible, un prix plus bas la rend suspecte : ce qui coûte moins semble faire moins.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Dans les trois cas, tu as réduit ta marge sans toucher la cause. Et tu as rendu la remontée plus difficile, parce qu'un prix qui baisse remonte mal.",
        },
        { type: ArticleBlockType.H2, text: "Où se trouve la réponse" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Pas dans un atelier de positionnement, et pas en relisant ta page. Elle est dans tes appels, parce que c'est le seul endroit où des gens ont dit avec leurs mots ce qu'ils vivaient, avant que tu ne le reformules.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Corriger de mémoire",
            items: [
              "Tu réécris avec tes mots à toi",
              "Tu te souviens des objections marquantes, pas des fréquentes",
              "Tu corriges ce qui te gêne, pas ce qui bloque",
              "Rien ne dit si ça a marché",
            ],
          },
          good: {
            title: "Corriger sur les appels",
            items: [
              "Tu reprends les formulations réellement employées",
              "Tu vois ce qui revient chez plusieurs personnes",
              "Tu identifies lequel des quatre blocages domine",
              "Les appels suivants disent si l'objection a disparu",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La dernière ligne est celle qui change tout. Une correction d'offre n'est pas une décision définitive, c'est une hypothèse. Ce qui te dit si elle était juste, ce sont les dix appels suivants : si l'objection qui revenait a disparu, tu avais vu juste.",
        },
        { type: ArticleBlockType.H2, text: "Trois questions à te poser maintenant" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Quelle phrase revient le plus souvent** quand les gens décrivent leur situation avant d'acheter ? C'est ton problème, formulé par eux.",
            "**Quelle objection revient au même moment** dans presque tous tes appels ? C'est ton blocage principal, et il a un endroit précis dans ta page.",
            "**Qu'est-ce que tes clients disent avoir gagné**, avec leurs chiffres à eux ? C'est ta promesse, et elle est plus crédible que celle que tu écrirais.",
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "On lit tes appels et on en sort ce qui se répète : la situation de départ, les objections et leur moment, les formulations employées. Ces éléments alimentent directement ton offre, son problème, sa promesse et ses preuves, avec les mots de ceux qui achètent plutôt que les tiens.",
          linkLabel: "Découvrir les offres",
          linkPath: ProtectedPaths.OFFERS,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Avant de toucher au prix, trouve lequel des quatre blocages te concerne, parce que trois d'entre eux empirent quand le prix baisse. Et cherche la réponse là où elle a été dite, dans tes appels, pas dans une relecture de ta propre page.",
        },
      ],
      faq: [
        {
          question: "Comment savoir si mon offre est trop chère ?",
          answer:
            "Le signe fiable est qu'on te demande une version réduite ou un étalement : la personne veut, comprend et croit, mais n'a pas le budget. Un simple « c'est trop cher » sans contre-proposition signale presque toujours autre chose, un problème non reconnu ou une promesse pas crédible.",
        },
        {
          question: "Faut-il baisser son prix quand on ne vend pas ?",
          answer:
            "Rarement. Sur les quatre causes possibles, une seule se corrige par le prix. Dans les trois autres, baisser réduit la marge sans toucher la cause, et rend la promesse moins crédible, parce que ce qui coûte moins semble faire moins.",
        },
        {
          question: "Comment reformuler une offre qui ne convertit pas ?",
          answer:
            "En repartant des mots employés par les gens dans tes appels, plutôt que des tiens. Cherche la phrase qui revient pour décrire la situation, l'objection qui tombe toujours au même moment, et ce que tes clients disent avoir gagné avec leurs propres chiffres.",
        },
        {
          question: "Comment savoir si la correction a marché ?",
          answer:
            "Sur les dix appels suivants. Si l'objection qui revenait a disparu ou s'est déplacée, l'hypothèse était bonne. Une correction d'offre n'est pas une décision définitive, c'est une hypothèse que les prochaines conversations valident ou non.",
        },
      ],
    },
    en: {
      title: "Your offer is not selling, and it is almost never the price",
      excerpt:
        "Cutting the price is the first reaction and the least effective one. When an offer does not sell, the problem is almost always upstream of the number.",
      description:
        "The four reasons an offer does not sell, how to tell them apart, and why the answer sits in your calls rather than in a positioning workshop.",
      ctaTitle: "Fix your offer on what was actually said",
      ctaText:
        "We go back to your calls to see where it breaks: the problem, the promise, the proof or the price. Then we rewrite the offer in your customers' words. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The meetings go well. People listen, find it interesting, ask for a proposal. And it stops there. After a few months the conclusion writes itself: it is too expensive.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "It is the most reassuring conclusion, because it can be fixed in a minute. It is also the wrong one in the vast majority of cases.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A price is judged too high when the value is not clear. So the problem is almost never the number, it is whatever should have made it obvious.",
        },
        { type: ArticleBlockType.H2, text: "The four blockers, and how to tell them apart" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An offer that does not sell is stuck at one of four points. They are not fixed the same way, and the first job is knowing which one is yours.",
        },
        { type: ArticleBlockType.H3, text: "1. The problem is not recognised" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You describe a problem the person does not have, or has without naming it that way. The tell: people listen politely, find it clever, and nobody says *that is exactly it*. No promise rescues a problem nobody recognises as theirs.",
        },
        { type: ArticleBlockType.H3, text: "2. Recognised, but not a priority" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Here you get *yes, that is true*, and then nothing happens. The problem exists, it is simply not expensive enough to jump ahead of the other ten. The tell: people postpone without ever saying no. What is missing is not an argument, it is a number.",
        },
        { type: ArticleBlockType.H3, text: "3. The promise is not credible" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The problem is recognised, it is a priority, and nobody believes you solve it. The tell: many questions about how, about how long, about what happens if it does not work. That is not a lack of appetite, it is a lack of proof.",
        },
        { type: ArticleBlockType.H3, text: "4. The price, genuinely" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "It happens, and it is recognisable: the person wants it, understands it, believes it, and has no budget. The unmistakable tell is that they ask for a smaller version or for staged payments. When someone just says *too expensive* and proposes nothing, you are in one of the three previous cases.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What you hear", "What is actually blocking"],
          rows: [
            ["Interesting, I will think about it", "The problem is not recognised"],
            ["True, but right now we are on something else", "Recognised, not a priority"],
            ["And concretely, how does it work?", "The promise lacks proof"],
            ["Would you have a lighter package?", "The price, and this one is real"],
          ],
        },
        { type: ArticleBlockType.H2, text: "Why cutting the price makes three cases worse" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If the problem is not recognised, a lower price does not make it appear. If it is not a priority, a lower price does not move it up the list. If the promise is not credible, a lower price makes it suspect: what costs less looks like it does less.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In all three you have cut your margin without touching the cause. And you have made recovery harder, because a price that goes down comes back up badly.",
        },
        { type: ArticleBlockType.H2, text: "Where the answer is" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Not in a positioning workshop, and not in rereading your own page. It is in your calls, because that is the only place where people said in their own words what they were living, before you reworded it.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Fixing from memory",
            items: [
              "You rewrite in your own words",
              "You remember the striking objections, not the frequent ones",
              "You fix what bothers you, not what blocks",
              "Nothing tells you whether it worked",
            ],
          },
          good: {
            title: "Fixing from the calls",
            items: [
              "You reuse the phrasings actually spoken",
              "You see what recurs across several people",
              "You identify which of the four blockers dominates",
              "The next calls tell you whether the objection is gone",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The last line is what changes everything. Fixing an offer is not a final decision, it is a hypothesis. What tells you whether it was right is the next ten calls: if the recurring objection has disappeared, you saw correctly.",
        },
        { type: ArticleBlockType.H2, text: "Three questions to ask yourself now" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Which sentence recurs most** when people describe their situation before buying? That is your problem, phrased by them.",
            "**Which objection lands at the same moment** in nearly every call? That is your main blocker, and it has a precise place on your page.",
            "**What do your customers say they gained**, in their own numbers? That is your promise, and it is more credible than one you would write.",
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "We read your calls and pull out what repeats: the starting situation, the objections and when they land, the phrasings used. That material feeds your offer directly, its problem, its promise and its proof, in the words of the people who buy rather than your own.",
          linkLabel: "See how offers work",
          linkPath: ProtectedPaths.OFFERS,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Before touching the price, find which of the four blockers is yours, because three of them get worse when the price drops. And look for the answer where it was said, in your calls, not in a rereading of your own page.",
        },
      ],
      faq: [
        {
          question: "How do I know if my offer is too expensive?",
          answer:
            "The reliable tell is being asked for a smaller version or staged payments: the person wants it, understands it and believes it, but has no budget. A plain too expensive with no counter-proposal almost always signals something else, an unrecognised problem or an unconvincing promise.",
        },
        {
          question: "Should you cut your price when you are not selling?",
          answer:
            "Rarely. Of the four possible causes, only one is fixed by price. In the other three, cutting reduces your margin without touching the cause, and makes the promise less credible, because what costs less looks like it does less.",
        },
        {
          question: "How do you rewrite an offer that does not convert?",
          answer:
            "By starting from the words people used in your calls rather than your own. Look for the sentence that recurs when describing the situation, the objection that always lands at the same moment, and what your customers say they gained in their own numbers.",
        },
        {
          question: "How do you know the fix worked?",
          answer:
            "Over the next ten calls. If the recurring objection has gone or moved, the hypothesis was right. Fixing an offer is not a final decision, it is a hypothesis that the next conversations confirm or not.",
        },
      ],
    },
  },
};
